<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { getUserInstitutions, getInstitutionName, updateUserInstitutionAccess, existsInstitution } from '@/components/utils/functions'
import BaseModal from '../BaseModal.vue'
interface IInstitutionData {
	id: string
	name: string
	access: boolean
}
const accessInstitutionId = ref<string>('')
const accessValue = ref<boolean>(true)
const store = useStore()
const institutionsData = ref<IInstitutionData[]>([])
const loading = ref(false)
const error = ref<string>('')
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref<string>('')

const obtenerInstituciones = async (): Promise<IInstitutionData[]> => {
	try {
		loading.value = true
		error.value = ''
		const instituciones = await getUserInstitutions(store.state.user.uid)
		const institutionArray = Object.entries(instituciones).map(([id, access]) => ({ id, access: Boolean(access) }))
		const data: IInstitutionData[] = []
		for (const institution of institutionArray) {
			const institutionName = await getInstitutionName(institution.id)
			data.push({ id: institution.id, name: institutionName, access: institution.access })
		}
		return data
	} catch (err: any) {
		console.error('Error al obtener instituciones:', err)
		return []
	} finally {
		loading.value = false
	}
}

// Cargar transacciones cuando el componente se monte
onMounted(async () => {
	institutionsData.value = await obtenerInstituciones()
})

const handleAccessForm = async () => {
	try {
		loading.value = true
		error.value = ''

		if (!accessInstitutionId.value.trim()) {
			error.value = 'El ID de la institución es requerido'
			showErrorModal.value = true
			loading.value = false
			return
		}

		// accessValue es un booleano, no es necesario convertir
		const hasAccess = accessValue.value
		const exists = await existsInstitution(accessInstitutionId.value.trim())
		if (exists) {
			await updateUserInstitutionAccess(store.state.user.uid, accessInstitutionId.value.trim(), hasAccess)

			// Recargar la lista de instituciones después de actualizar
			institutionsData.value = await obtenerInstituciones()

			// Limpiar el formulario
			accessInstitutionId.value = ''
			accessValue.value = true

			successMessage.value = 'Acceso actualizado exitosamente'
			showSuccessModal.value = true
		} else {
			error.value = `La institución ${accessInstitutionId.value.trim()} no existe`
			showErrorModal.value = true
			return
		}
	} catch (err: any) {
		console.error('Error al actualizar acceso a institución:', err)
		error.value = err.message || 'Error al actualizar el acceso. Verifica que tengas permisos de administrador.'
		showErrorModal.value = true
	} finally {
		loading.value = false
	}
}
</script>
<template>
	<div>
		<!-- Estado de carga -->
		<div v-if="loading" class="loading-state">
			<div class="loader"></div>
			<p class="loading-text">Cargando instituciones...</p>
		</div>

		<!-- Contenido principal -->
		<va-card v-else class="bitacora-card" bordered>
			<va-card-title class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="receipt_long" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Instituciones</h2>
							<p class="header-subtitle">{{ institutionsData.length }} institución{{ institutionsData.length !== 1 ? 'es' : '' }} registrada{{ institutionsData.length !== 1 ? 's' : '' }}</p>
						</div>
					</div>
				</div>
			</va-card-title>
			<va-card-content class="table-content">
				<div class="content-layout">
					<!-- Tabla de instituciones -->
					<div class="table-section">
						<div class="table-wrapper">
							<div class="table-scroll-container">
								<div class="va-table-responsive">
									<table class="va-table va-table--striped">
										<thead>
											<tr>
												<th>Id de la institución</th>
												<th>Nombre de la institución</th>
												<th>Acceso a la institución</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="institution in institutionsData" :key="institution.id">
												<td>{{ institution.id }}</td>
												<td>{{ institution.name }}</td>
												<td>
													<VaBadge :text="institution.access ? 'Activo' : 'Inactivo'" :color="institution.access ? 'success' : 'danger'" />
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>

					<!-- Formulario de acceso -->
					<div class="form-section">
						<va-card class="access-form-card" bordered>
							<va-card-title class="form-header">
								<div class="form-header-content">
									<va-icon name="add_circle" class="form-header-icon" />
									<h3 class="form-title">Dar acceso a institución</h3>
								</div>
							</va-card-title>
							<va-card-content class="form-content">
								<form @submit.prevent="handleAccessForm" class="institution-access-form">
									<div class="form-field">
										<label for="institutionId" class="form-label">
											<va-icon name="business" class="label-icon" />
											ID de la institución
										</label>
										<va-input v-model="accessInstitutionId" id="institutionId" type="text" required placeholder="Ej: 37" class="form-input" />
									</div>

									<div class="form-field">
										<label for="accessStatus" class="form-label">
											<va-icon name="lock" class="label-icon" />
											Estado de acceso
										</label>
										<VaRadio
											v-model="accessValue"
											:options="[
												{ text: 'Activo', value: true },
												{ text: 'Inactivo', value: false }
											]"
											value-by="value"
											text-by="text" />
									</div>

									<va-button type="submit" color="primary" :disabled="loading" class="submit-button" block>
										<va-icon name="save" class="button-icon" />
										{{ loading ? 'Guardando...' : 'Guardar acceso' }}
									</va-button>
								</form>
							</va-card-content>
						</va-card>
					</div>
				</div>
			</va-card-content>
		</va-card>
	</div>
	<BaseModal v-model="showErrorModal" title="Error" :message="error" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Header mejorado */
.header-gradient {
	background: #062342;
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.header-icon-wrapper {
	width: 34px;
	height: 34px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

.header-subtitle {
	font-size: 1rem;
	color: rgba(255, 255, 255, 0.9);
	margin: 0.5rem 0 0 0;
	font-weight: 500;
}

.table-content {
	padding: 1.5rem;
	background: #fafbfc;
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

/* Layout de dos columnas */
.content-layout {
	display: grid;
	grid-template-columns: 1fr 400px;
	gap: 1.5rem;
	align-items: start;
}

.table-section {
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	overflow: hidden;
	position: relative;
	z-index: 0;
	display: flex;
	flex-direction: column;
	height: 100%;
	max-height: calc(100vh - 250px);
}

.table-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
}

.table-scroll-container {
	overflow-y: auto;
	overflow-x: auto;
	flex: 1;
	min-height: 0;
	max-height: 100%;
	scrollbar-width: thin;
	scrollbar-color: #94a3b8 #f1f5f9;
}

.table-section .va-table-responsive {
	overflow: visible;
}

.table-section table {
	width: 100%;
	border-collapse: collapse;
}

.table-section thead {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	/* Mantener sticky sin tapar overlays (selects/menus/modals) del layout */
	z-index: 1;
}

.table-section th {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
	text-align: left;
}

.table-section tbody tr {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

.table-section tbody tr:hover {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
}

.table-section tbody td {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

.form-section {
	position: sticky;
	top: 1.5rem;
}

/* Estilos del formulario */
.access-form-card {
	border-radius: 16px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
}

.access-form-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.form-header {
	background: #062342;
	padding: 1.25rem 1.5rem;
	border-radius: 0;
}

.form-header-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.form-header-icon {
	color: white;
	font-size: 1.5rem;
}

.form-title {
	margin: 0;
	color: white;
	font-size: 1.25rem;
	font-weight: 700;
	letter-spacing: -0.3px;
}

.form-content {
	padding: 1.5rem;
	background: white;
}

.institution-access-form {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.form-label {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-weight: 600;
	color: #1a202c;
	font-size: 0.875rem;
	margin-bottom: 0.25rem;
}

.label-icon {
	color: #062342;
	font-size: 1rem;
}

.form-input {
	width: 100%;
}

.submit-button {
	margin-top: 0.5rem;
	padding: 0.875rem 1.5rem;
	font-weight: 600;
	font-size: 0.9375rem;
	border-radius: 8px;
	transition: all 0.2s ease;
}

.submit-button:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(062342, 0.3);
}

.button-icon {
	margin-right: 0.5rem;
}

:deep(.scrollable-table .va-data-table) {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table__table-wrapper) {
	overflow: visible !important;
	height: auto !important;
}

:deep(.scrollable-table table) {
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table) {
	background: transparent;
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table thead) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	z-index: 10;
}

:deep(.scrollable-table .va-data-table__table thead th) {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
}

:deep(.scrollable-table .va-data-table__table tbody tr) {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

:deep(.scrollable-table .va-data-table__table tbody tr:hover) {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

:deep(.scrollable-table .va-data-table__table tbody td) {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

/* Estado de carga */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 4rem 2rem;
	text-align: center;
	background: white;
	border-radius: 20px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
}

.loader {
	border: 4px solid #f3f4f6;
	border-radius: 50%;
	border-top: 4px solid #062342;
	width: 60px;
	height: 60px;
	animation: spin 1s linear infinite;
	box-shadow: 0 4px 12px rgba(062342, 0.2);
	margin-bottom: 1.5rem;
}

.loading-text {
	margin-top: 1rem;
	color: #6c757d;
	font-size: 1rem;
	font-weight: 500;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

/* Scrollbar personalizado para la tabla */
.table-scroll-container::-webkit-scrollbar {
	width: 10px;
	height: 10px;
}

.table-scroll-container::-webkit-scrollbar-track {
	background: #f1f5f9;
	border-radius: 5px;
	margin: 4px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
	background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
	border-radius: 5px;
	border: 2px solid #f1f5f9;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
	background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

/* Responsive */
@media (max-width: 1024px) {
	.content-layout {
		grid-template-columns: 1fr;
		gap: 1.5rem;
	}

	.form-section {
		position: static;
	}

	.table-section {
		max-height: calc(100vh - 300px);
	}

	.table-content {
		max-height: calc(100vh - 200px);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.content-layout {
		grid-template-columns: 1fr;
		gap: 1rem;
	}

	.form-section {
		position: static;
	}

	.table-section {
		max-height: calc(100vh - 280px);
	}

	.table-content {
		padding: 1rem;
		max-height: calc(100vh - 180px);
	}

	.form-content {
		padding: 1.25rem;
	}

	:deep(.scrollable-table .va-data-table__table thead th),
	:deep(.scrollable-table .va-data-table__table tbody td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.8125rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}

	.content-layout {
		grid-template-columns: 1fr;
		gap: 1rem;
	}

	.table-content {
		padding: 1rem;
	}

	.form-header {
		padding: 1rem 1.25rem;
	}

	.form-title {
		font-size: 1.125rem;
	}

	.form-content {
		padding: 1rem;
	}
}

.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}
}
</style>
